// LineasPanelXL - Sprint 44 Fase 3 Vista 4.
// Panel de lineas GENERICO para cotizacion/venta. Consolida los dos
// LineasPanel legacy (428L) en un solo componente con prop docType y
// objeto api inyectado por el padre con las funciones:
//   listLineas(id), createLinea(id,payload), updateLinea(lid,payload),
//   deleteLinea(lid), recalcDoc(id), updateDoc(id, patch).
//
// Reusa ProductPickerLite legacy. Fila extraida en LineaRowXL.jsx.
// XLSkeleton en loading. Auto-heal S45 (recalc si totales inconsistentes).
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import XLSkeleton from '../xl/XLSkeleton';
import TotalesXL from './TotalesXL';
import LineaRowXL from './LineaRowXL';
import ProductPickerLite from '../shared/ProductPickerLite';

const CSS_ID = 'xl-lineas-panel-css';
const CSS = `
.xl-lineas { display: flex; flex-direction: column; gap: 16px; font-family: var(--xl-font-sans) }
.xl-lineas__addbar {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.xl-lineas__addbar-lbl {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
}
.xl-lineas__toggle {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px;
  color: var(--xl-color-text);
}
.xl-lineas__toggle-hint {
  font-size: 11px;
  color: var(--xl-color-text-muted);
}
.xl-lineas__table-wrap {
  overflow-x: auto;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-surface);
  box-shadow: var(--xl-shadow-sm);
}
.xl-lineas__table { width: 100%; min-width: 780px; border-collapse: collapse; font-size: 13px }
.xl-lineas__table thead th {
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  white-space: nowrap;
}
.xl-lineas__table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
  vertical-align: middle;
}
.xl-lineas__table tr:last-child td { border-bottom: 0 }
.xl-lineas__empty {
  padding: 28px 16px; text-align: center;
  color: var(--xl-color-text-muted); font-size: 13px;
}
.xl-lineas__mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-td-right { text-align: right }
.xl-lineas__numinp {
  width: 80px;
  font: 400 13px/1.4 var(--xl-font-mono);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  padding: 6px 8px;
  text-align: right;
  box-sizing: border-box;
}
.xl-lineas__numinp:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-lineas__numinp:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-lineas__numinp:disabled {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-muted);
  cursor: not-allowed;
}
.xl-lineas__del {
  background: transparent; border: none; cursor: pointer;
  color: var(--xl-color-danger);
  font-size: 13px; padding: 4px 8px;
  border-radius: var(--xl-radius-sm);
}
.xl-lineas__del:hover { background: rgba(220,38,38,0.08) }
.xl-lineas__del:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-lineas__err {
  padding: 10px 14px;
  border-radius: var(--xl-radius-md);
  background: rgba(220,38,38,0.08);
  color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.25);
  font-size: 13px;
}
.xl-lineas__placeholder {
  padding: 16px;
  color: var(--xl-color-text-muted);
  font-size: 13px;
}
`;

export default function LineasPanelXL({
  docId, docType, isNew, isEditable, moneda = 'COP',
  doc, api, onChanged,
}) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState(null);

  const load = useCallback(async () => {
    if (isNew || !docId || !api) { setItems([]); return; }
    setLoading(true); setErr(null);
    try {
      const ls = await api.listLineas(docId);
      setItems(ls || []);
      const needsRecalc = doc && Number(doc.total) > 0
        && Number(doc.subtotal || 0) === 0
        && (ls || []).some((li) => Number(li.iva_pct || 0) > 0);
      if (needsRecalc && api.recalcDoc) {
        try {
          await api.recalcDoc(docId);
          const ls2 = await api.listLineas(docId);
          setItems(ls2 || []);
          if (onChanged) onChanged();
        } catch (_) { /* no bloquea */ }
      }
    } catch (e) {
      setErr((e && e.message) || 'Error al cargar lineas');
    } finally {
      setLoading(false);
    }
  }, [docId, isNew, api, doc, onChanged]);

  useEffect(() => { load(); }, [load]);

  const notify = () => { if (onChanged) onChanged(); };

  const onAddProducto = async (p) => {
    if (!isEditable || !api) return;
    try {
      await api.createLinea(docId, {
        producto_id: p.id,
        producto_descripcion: p.nombre || p.name,
        cantidad: 1,
        precio_unitario: p.precio_venta != null
          ? p.precio_venta
          : (p.sale_price != null ? p.sale_price : 0),
        descuento: 0,
      });
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onEditField = async (linea, field, value) => {
    if (!isEditable || !api) return;
    try {
      await api.updateLinea(linea.id, { [field]: value });
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onDelete = async (linea) => {
    if (!isEditable || !api) return;
    if (!window.confirm('Eliminar esta linea?')) return;
    try {
      await api.deleteLinea(linea.id);
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onTogglePrecioIncluye = async (val) => {
    if (!isEditable || !docId || !api) return;
    try {
      await api.updateDoc(docId, { precio_incluye_iva: val });
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  if (isNew) {
    const tipo = docType === 'venta' ? 'venta' : 'cotizacion';
    return (
      <div className="xl-lineas__placeholder" role="note">
        Guarda la {tipo} primero para poder agregar lineas.
      </div>
    );
  }

  const incluye = !!(doc && doc.precio_incluye_iva);

  return (
    <div className="xl-lineas">
      {isEditable && (
        <div className="xl-lineas__addbar">
          <div>
            <div className="xl-lineas__addbar-lbl">Agregar producto</div>
            <ProductPickerLite onPick={onAddProducto} />
          </div>
          <label className="xl-lineas__toggle">
            <input
              type="checkbox"
              checked={incluye}
              onChange={(e) => onTogglePrecioIncluye(e.target.checked)}
            />
            <span>Precio incluye IVA</span>
            <span className="xl-lineas__toggle-hint">
              (toggle global del documento)
            </span>
          </label>
        </div>
      )}

      {err && <div className="xl-lineas__err" role="alert">{err}</div>}

      <div className="xl-lineas__table-wrap">
        <table className="xl-lineas__table" role="table" aria-label="Lineas del documento">
          <thead>
            <tr>
              <th>Producto</th>
              <th className="xl-td-right">Cant.</th>
              <th className="xl-td-right">Precio</th>
              <th className="xl-td-right">Desc. %</th>
              <th className="xl-td-right">IVA</th>
              <th className="xl-td-right">Base</th>
              <th className="xl-td-right">IVA $</th>
              <th className="xl-td-right">Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <tr key={'sk-' + i}>
                <td colSpan={9}>
                  <XLSkeleton variant="line" width="90%" height={18} />
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={9} className="xl-lineas__empty">Sin lineas.</td>
              </tr>
            )}
            {!loading && items.map((l) => (
              <LineaRowXL
                key={l.id}
                linea={l}
                isEditable={isEditable}
                moneda={moneda}
                onEditField={onEditField}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>

      {doc && <TotalesXL doc={doc} moneda={moneda} />}
    </div>
  );
}
